import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { VideoCamera, Plus, PencilSimple, Trash, Play, X, ArrowSquareOut, Users } from "@phosphor-icons/react";

// Converte URLs do YouTube / Vimeo / Google Drive em URL embutível (<iframe>).
export function toEmbedUrl(raw) {
  if (!raw) return { embed: null, provider: "unknown" };
  const url = raw.trim();
  try {
    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    if (yt) return { embed: `https://www.youtube.com/embed/${yt[1]}`, provider: "youtube" };
    const vm = url.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return { embed: `https://player.vimeo.com/video/${vm[1]}`, provider: "vimeo" };
    const gd = url.match(/drive\.google\.com\/file\/d\/([A-Za-z0-9_-]+)/);
    if (gd) return { embed: `https://drive.google.com/file/d/${gd[1]}/preview`, provider: "drive" };
    if (/^https?:\/\//i.test(url)) return { embed: url, provider: "external" };
  } catch (e) { /* fallthrough */ }
  return { embed: null, provider: "unknown" };
}

const EMPTY = { title: "", description: "", video_url: "", student_ids: [] };

export default function LessonsPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "teacher";
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [player, setPlayer] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/lessons");
      setItems(r.data);
      if (canManage) {
        const s = await api.get("/students");
        setStudents(s.data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao carregar aulas");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function openEdit(l) {
    setEditing(l);
    setForm({
      title: l.title,
      description: l.description || "",
      video_url: l.video_url,
      student_ids: Array.isArray(l.student_ids) ? l.student_ids : [],
    });
    setOpen(true);
  }
  function toggleStudent(id) {
    setForm(f => ({
      ...f,
      student_ids: f.student_ids.includes(id) ? f.student_ids.filter(x => x !== id) : [...f.student_ids, id],
    }));
  }

  async function save() {
    if (!form.title.trim() || !form.video_url.trim()) { toast.error("Título e URL do vídeo são obrigatórios"); return; }
    const { embed } = toEmbedUrl(form.video_url);
    if (!embed) { toast.error("URL inválida. Use YouTube, Vimeo, Google Drive ou link direto."); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/lessons/${editing.id}`, form);
      else await api.post("/lessons", form);
      toast.success(editing ? "Aula atualizada" : "Aula cadastrada");
      setOpen(false); setForm(EMPTY); setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao salvar");
    } finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm("Excluir esta aula?")) return;
    try {
      await api.delete(`/lessons/${id}`);
      toast.success("Aula excluída"); load();
    } catch (e) { toast.error("Erro ao excluir"); }
  }

  function visibilityLabel(l) {
    const n = (l.student_ids || []).length;
    if (n === 0) return "Todos os alunos";
    return `${n} aluno(s)`;
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-6xl mx-auto space-y-6">
      <header className="flex items-end justify-between fade-up flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E5FFF]">Vídeo Aulas</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-2">
            {canManage ? "Minhas Aulas" : "Aulas Disponíveis"}
          </h1>
          <p className="text-zinc-500 text-sm mt-2">
            {canManage
              ? "Cadastre links de vídeo (YouTube, Vimeo, Google Drive) e escolha quais alunos devem visualizá-los."
              : "Assista às aulas gravadas pelo seu professor. Clique em uma aula para abrir o player."}
          </p>
        </div>
        {canManage && (
          <Button data-testid="lesson-new" onClick={openCreate} className="bg-[#002FA7] hover:bg-[#001f7a] brutal-shadow">
            <Plus size={16} className="mr-2" weight="bold" /> Nova aula
          </Button>
        )}
      </header>

      {loading ? (
        <div className="text-center py-16 text-zinc-400">Carregando…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-zinc-400 border border-dashed border-zinc-300 rounded-md dot-grid">
          {canManage ? "Nenhuma aula cadastrada. Clique em 'Nova aula' para começar." : "Seu professor ainda não cadastrou aulas."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 fade-up">
          {items.map(l => {
            const { provider } = toEmbedUrl(l.video_url);
            const badge = { youtube: "YouTube", vimeo: "Vimeo", drive: "Google Drive", external: "Link", unknown: "Link" }[provider];
            return (
              <div key={l.id} data-testid={`lesson-${l.id}`} className="bg-white border border-zinc-200 rounded-md p-5 flex flex-col hover:border-zinc-400 transition-colors">
                <div className="flex items-start gap-3">
                  <VideoCamera size={22} weight="duotone" color="#002FA7" />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-base truncate">{l.title}</div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mt-1">{badge}</div>
                  </div>
                </div>
                {l.description && <p className="text-sm text-zinc-600 mt-3 line-clamp-3">{l.description}</p>}
                {canManage && (
                  <div className="mt-3 flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold">
                    <Users size={12} weight="duotone" className="text-zinc-500" />
                    <span className={(l.student_ids || []).length === 0 ? "text-[#002FA7]" : "text-zinc-500"}>
                      {visibilityLabel(l)}
                    </span>
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  <Button data-testid={`play-${l.id}`} size="sm" onClick={() => setPlayer(l)} className="flex-1 bg-[#002FA7] hover:bg-[#001f7a]">
                    <Play size={14} className="mr-1" weight="fill" /> Assistir
                  </Button>
                  {canManage && (
                    <>
                      <Button data-testid={`edit-${l.id}`} variant="outline" size="sm" onClick={() => openEdit(l)}><PencilSimple size={14} /></Button>
                      <Button data-testid={`del-${l.id}`} variant="ghost" size="sm" onClick={() => remove(l.id)}><Trash size={14} /></Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog: Create/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar aula" : "Nova aula"}</DialogTitle>
            <DialogDescription>Preencha os campos e escolha quais alunos verão a aula.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Título</Label><Input data-testid="l-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-2" /></div>
            <div><Label>Descrição</Label><Textarea data-testid="l-desc" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-2" rows={3} /></div>
            <div>
              <Label>URL do vídeo (YouTube, Vimeo, Google Drive)</Label>
              <Input data-testid="l-url" placeholder="https://www.youtube.com/watch?v=..." value={form.video_url} onChange={e => setForm({...form, video_url: e.target.value})} className="mt-2" />
              <div className="text-xs text-zinc-500 mt-1">Ex.: YouTube, Vimeo, ou link "compartilhável" do Google Drive.</div>
            </div>
            <div>
              <Label>Visibilidade</Label>
              <div className="text-xs text-zinc-500 mt-1 mb-2">
                {form.student_ids.length === 0
                  ? "Todos os alunos vinculados verão esta aula."
                  : `Apenas ${form.student_ids.length} aluno(s) selecionado(s) verão esta aula.`}
              </div>
              <div className="max-h-40 overflow-y-auto border border-zinc-200 rounded p-2 space-y-1">
                {students.map(s => (
                  <label key={s.id} className="flex items-center gap-2 text-sm py-1 hover:bg-zinc-50 rounded px-1 cursor-pointer">
                    <Checkbox data-testid={`l-student-${s.id}`} checked={form.student_ids.includes(s.id)} onCheckedChange={() => toggleStudent(s.id)} />
                    <span className="flex-1">{s.name}</span>
                    <span className="text-xs text-zinc-400">{s.email}</span>
                  </label>
                ))}
                {students.length === 0 && <div className="text-xs text-zinc-400 py-4 text-center">Nenhum aluno cadastrado.</div>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="l-submit" onClick={save} disabled={saving} className="bg-[#002FA7] hover:bg-[#001f7a]">
              {saving ? "Salvando…" : (editing ? "Salvar alterações" : "Cadastrar aula")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {player && <PlayerModal lesson={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}

function PlayerModal({ lesson, onClose }) {
  const { embed } = toEmbedUrl(lesson.video_url);
  return (
    <div data-testid="lesson-player" className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-4xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <div className="min-w-0">
            <div className="font-heading font-black text-lg truncate">{lesson.title}</div>
            {lesson.description && <div className="text-xs text-zinc-500 line-clamp-1">{lesson.description}</div>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a href={lesson.video_url} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[#002FA7]" title="Abrir original">
              <ArrowSquareOut size={20} />
            </a>
            <button data-testid="player-close" onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-zinc-100 flex items-center justify-center"><X size={18} /></button>
          </div>
        </div>
        <div className="aspect-video bg-black">
          {embed ? (
            <iframe
              src={embed}
              title={lesson.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white text-sm">
              URL de vídeo inválida. <a className="underline ml-1" href={lesson.video_url} target="_blank" rel="noreferrer">Abrir link</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
